import { create } from 'zustand'
import { supabase } from '@/shared/lib/supabase/client'

// Both clients read one feed. mobile_notifications is that feed, chosen because the Play
// Store app already reads it, so unifying moved the web side rather than the shipped one.
// Storage uses type/category/is_read and carries per client routing in `data`: the Flutter
// app navigates by data.route, connector-web by data.web_route. This store adapts that shape
// to the flatter one the UI wants, so the page does not need to know either detail.
export interface AppNotification {
  id: string
  category: string
  type: string
  title: string
  body: string | null
  link_path: string | null
  read_at: string | null
  created_at: string
}

interface FeedRow {
  id: string
  type: string | null
  category: string | null
  title: string | null
  body: string | null
  data: Record<string, unknown> | null
  is_read: boolean | null
  created_at: string
}

function toNotification(row: FeedRow): AppNotification {
  const webRoute = row.data?.['web_route']
  return {
    id: row.id,
    category: row.category ?? 'General',
    type: row.type ?? 'generic',
    title: row.title ?? '',
    body: row.body || null,
    link_path: typeof webRoute === 'string' && webRoute ? webRoute : null,
    // Storage keeps a boolean, the UI only asks whether it is unread. The timestamp is not
    // recorded upstream, so a truthy marker stands in for it.
    read_at: row.is_read ? row.created_at : null,
    created_at: row.created_at,
  }
}

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
  loading: boolean
  load: (userId: string) => Promise<void>
  subscribe: (userId: string) => () => void
  markAllRead: () => Promise<void>
  markRead: (id: string) => Promise<void>
  dismiss: (id: string) => Promise<void>
  clearAll: (userId: string) => Promise<void>
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: true,

  load: async (userId: string) => {
    const { data } = await supabase
      .from('mobile_notifications')
      .select('id, type, category, title, body, data, is_read, created_at')
      .eq('connector_id', userId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(50)

    const notifications = ((data ?? []) as FeedRow[]).map(toNotification)
    set({
      notifications,
      unreadCount: notifications.filter((n) => !n.read_at).length,
      loading: false,
    })
  },

  subscribe: (userId: string) => {
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'mobile_notifications',
          filter: `connector_id=eq.${userId}`,
        },
        (payload) => {
          const notif = toNotification(payload.new as FeedRow)
          set((state) => ({
            notifications: [notif, ...state.notifications],
            unreadCount: state.unreadCount + 1,
          }))
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  },

  markAllRead: async () => {
    const ids = get()
      .notifications.filter((n) => !n.read_at)
      .map((n) => n.id)
    if (ids.length === 0) return

    await supabase.from('mobile_notifications').update({ is_read: true }).in('id', ids)
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        read_at: n.read_at ?? n.created_at,
      })),
      unreadCount: 0,
    }))
  },

  markRead: async (id: string) => {
    const notif = get().notifications.find((n) => n.id === id)
    if (!notif || notif.read_at) return

    await supabase.from('mobile_notifications').update({ is_read: true }).eq('id', id)
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, read_at: n.created_at } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }))
  },

  // Dismissing is a soft delete, matching what the mobile app already does, so the row
  // survives long enough for the retention job to remove it and a mistake is not permanent.
  dismiss: async (id: string) => {
    const notif = get().notifications.find((n) => n.id === id)
    if (!notif) return

    await supabase.from('mobile_notifications').update({ is_deleted: true }).eq('id', id)
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount: notif.read_at ? state.unreadCount : Math.max(0, state.unreadCount - 1),
    }))
  },

  clearAll: async (userId: string) => {
    await supabase
      .from('mobile_notifications')
      .update({ is_deleted: true })
      .eq('connector_id', userId)
      .eq('is_deleted', false)

    set({ notifications: [], unreadCount: 0 })
  },
}))
